import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, Transition as _Transition, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "header" }
const _hoisted_2 = {
  key: 1,
  class: "room-title-wrapper"
}
const _hoisted_3 = {
  key: 0,
  class: "copy-feedback"
}
const _hoisted_4 = {
  key: 2,
  class: "room-title-wrapper-game"
}
const _hoisted_5 = {
  key: 3,
  class: "state-panel"
}
const _hoisted_6 = {
  key: 4,
  class: "state-panel"
}

import { onMounted, onUnmounted, computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useGameStore } from '@/stores/gameStore';
import type { Roles } from '@/types/game';
import NotificationDisplay from '@/views/ui/NotificationDisplay.vue';
import LobbyLayout from './wait/LobbyLayout.vue';
import GameLayout from '@/views/game/GameLayout.vue';
import PhaseAnnouncer from '@/views/game/PhaseAnnouncer.vue';
import SpecialAnnouncement from '@/views/game/SpecialAnnouncement.vue';
import ErrorDisplay from '@/views/ui/ErrorDisplay.vue';
import GameOverScreen from '@/views/game/GameOverScreen.vue';
import IntroductionForm from '@/views/game/IntroductionForm.vue';
import DayResultsPanel from '@/views/game/DayResultsPanel.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'RoomView',
  setup(__props) {

const gameStore = useGameStore();
const route = useRoute();
const showAnnouncer = ref(false);
const announcerTitle = ref('');
const announcerSubtitle = ref('');
const isRoleAnnouncement = ref(false);
const isCopied = ref(false);
const selectedPlayerName = ref<string | null>(null);

const roleMap: Record<string, string> = {
  mafia: "Мафия", citizen: "Мирный житель", doctor: "Доктор", comissar: "Комиссар", whore: "Потаскуха"
};
const phaseMap: Record<string, string> = {
  introduction_night: "Ночь знакомств", introduction_day: "Первый день", joke_voting: "Первое голосование", night: "Ночь", day: "День", voting: "Голосование", game_over: "Конец игры"
};
const roleSubtitleMap: Record<string, string> = {
  mafia: "Ваша цель — истребить всех мирных жителей.",
  citizen: "Ваша цель — найти и казнить всю мафию.",
  doctor: "Каждую ночь вы можете спасти кого-то от смерти. Помогайте мирным жителям.",
  comissar: "Каждую ночь вы можете проверить одного игрока. Истрибите мафию.",
  whore: "Каждую ночь вы можете лишить одного игрока действия"
};

watch([() => gameStore.room?.status, () => gameStore.room?.phase], ([newStatus, newPhase], [oldStatus, oldPhase]) => {
  if (oldStatus === 'waiting' && newStatus === 'in_progress') {
    const myRole = gameStore.myRole || '';
    announcerTitle.value = roleMap[myRole] || myRole;
    announcerSubtitle.value = roleSubtitleMap[myRole] || 'Выполняйте цели своей роли для победы.';
    isRoleAnnouncement.value = true;
    showAnnouncer.value = true;
    return;
  }
  if (newPhase && newPhase !== oldPhase) {
    if (isRoleAnnouncement.value && showAnnouncer.value) return;
    announcerTitle.value = phaseMap[newPhase] || 'Новая фаза';
    announcerSubtitle.value = '';
    isRoleAnnouncement.value = false;
    showAnnouncer.value = true;
    setTimeout(() => { showAnnouncer.value = false; }, 3500);
  }
});

const shouldShowResultsPanel = computed(() => {
  return (gameStore.room?.last_events?.length ?? 0) > 0 && !gameStore.isGameOver;
});

const isAlive = computed(() => gameStore.myPlayer?.is_alive ?? false);

const handleAnnouncerClose = () => {
  const wasRoleAnnouncement = isRoleAnnouncement.value;
  showAnnouncer.value = false;
  if (wasRoleAnnouncement) {
    const currentPhase = gameStore.room?.phase;
    if (currentPhase && phaseMap[currentPhase]) {
      setTimeout(() => {
        announcerTitle.value = phaseMap[currentPhase];
        announcerSubtitle.value = '';
        isRoleAnnouncement.value = false;
        showAnnouncer.value = true;
        setTimeout(() => { showAnnouncer.value = false; }, 3500);
      }, 500);
    }
  }
};

watch(() => gameStore.room?.phase, () => {
  selectedPlayerName.value = null;
});

const copyRoomId = async () => {
  if (!gameStore.room?.room_id || isCopied.value) return;
  try {
    await navigator.clipboard.writeText(gameStore.room.room_id);
    isCopied.value = true;
    setTimeout(() => { isCopied.value = false; }, 2000);
  } catch (err) {
    console.error('Не удалось скопировать ID комнаты:', err);
  }
};

const handlePlayerSelect = (playerName: string) => {
  const newSelectedPlayer = selectedPlayerName.value === playerName ? null : playerName;
  selectedPlayerName.value = newSelectedPlayer;
  if (gameStore.isNightActionPhase || gameStore.isVotingPhase) {
    gameStore.selectTeamTarget(newSelectedPlayer);
  }

  if (gameStore.myPlayerHasActed) {
    setTimeout(() => { selectedPlayerName.value = null; }, 300);
  }
};

const submitDescription = (description: string) => {
  gameStore.performAction('introduce', { description });
};

onMounted(async () => {
  const roomId = route.params.id as string;
  if (!gameStore.room || gameStore.room.room_id !== roomId) {
    await gameStore.fetchRoomDetails(roomId);
  }
  if (gameStore.room?.room_id === roomId) {
    gameStore.connectWebSocket();
  }
});

onUnmounted(() => {
  if (route.name === 'Room') {
    return;
  }
  gameStore.disconnectWebSocket();
});

const handleUpdateRoles = (newRoles: Roles) => {
  gameStore.setRoles(newRoles);
};

const handleUpdateEnvironment = (newEnvironment: string | null) => {
  gameStore.setEnvironment(newEnvironment);
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["room-view", {
    'is-night': _unref(gameStore).isNight,
    'is-dead': !isAlive.value
  }])
  }, [
    _createVNode(PhaseAnnouncer, {
      show: showAnnouncer.value,
      title: announcerTitle.value,
      subtitle: announcerSubtitle.value,
      "is-role": isRoleAnnouncement.value,
      role: _unref(gameStore).myRole || undefined,
      onClose: handleAnnouncerClose
    }, null, 8, ["show", "title", "subtitle", "is-role", "role"]),
    _createVNode(ErrorDisplay, {
      message: _unref(gameStore).error,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (_unref(gameStore).clearError()))
    }, null, 8, ["message"]),
    _createVNode(SpecialAnnouncement),
    _createVNode(NotificationDisplay),
    (_unref(gameStore).isGameOver)
      ? (_openBlock(), _createBlock(GameOverScreen, { key: 0 }))
      : _createCommentVNode("", true),
    (shouldShowResultsPanel.value)
      ? (_openBlock(), _createBlock(DayResultsPanel, { key: 1 }))
      : (_unref(gameStore).room)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass(["room-content", { 'in-game': _unref(gameStore).room.status === 'in_progress' }])
          }, [
            _createElementVNode("div", _hoisted_1, [
              (!_unref(gameStore).isGameOver)
                ? (_openBlock(), _createBlock(_component_router_link, {
                    key: 0,
                    to: "/",
                    onClick: _unref(gameStore).leaveRoom,
                    class: "back-btn",
                    title: "Покинуть комнату"
                  }, {
                    default: _withCtx(() => _cache[1] || (_cache[1] = [
                      _createElementVNode("span", { class: "back-btn-arrow" }, "←", -1),
                      _createElementVNode("span", { class: "back-btn-text" }, "Назад в лобби", -1)
                    ])),
                    _: 1,
                    __: [1]
                  }, 8, ["onClick"]))
                : _createCommentVNode("", true),
              (_unref(gameStore).room.status === 'waiting')
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createElementVNode("div", null, [
                      _createElementVNode("h1", {
                        onClick: copyRoomId,
                        title: "Нажмите, чтобы скопировать ID"
                      }, [
                        _createTextVNode(" Комната #" + _toDisplayString(_unref(gameStore).room.room_id) + " ", 1),
                        _cache[2] || (_cache[2] = _createElementVNode("svg", {
                          class: "copy-icon",
                          xmlns: "http://www.w3.org/2000/svg",
                          viewBox: "0 0 24 24",
                          fill: "currentColor"
                        }, [
                          _createElementVNode("path", { d: "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-5zm0 16H8V7h11v14z" })
                        ], -1))
                      ]),
                      _createVNode(_Transition, { name: "fade-fast" }, {
                        default: _withCtx(() => [
                          (isCopied.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "Код скопирован!"))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ])
                  ]))
                : _createCommentVNode("", true),
              (_unref(gameStore).room.status === 'in_progress')
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("h1", null, "Комната #" + _toDisplayString(_unref(gameStore).room.room_id), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            (_unref(gameStore).room.status === 'waiting')
              ? (_openBlock(), _createBlock(LobbyLayout, {
                  key: 0,
                  players: _unref(gameStore).room.players,
                  roles: _unref(gameStore).room.roles,
                  "initial-environment": _unref(gameStore).room.environ,
                  "is-host": _unref(gameStore).isHost,
                  "player-count": _unref(gameStore).playerCount,
                  "is-loading": _unref(gameStore).isLoading,
                  onUpdateRoles: handleUpdateRoles,
                  onUpdateEnvironment: handleUpdateEnvironment
                }, null, 8, ["players", "roles", "initial-environment", "is-host", "player-count", "is-loading"]))
              : (_unref(gameStore).room.phase === 'introduction_night' && !_unref(gameStore).myPlayerHasActed)
                ? (_openBlock(), _createBlock(IntroductionForm, {
                    key: 1,
                    onSubmitDescription: submitDescription
                  }))
                : (_unref(gameStore).room.status === 'in_progress')
                  ? (_openBlock(), _createBlock(GameLayout, {
                      key: 2,
                      "selected-player-name": selectedPlayerName.value,
                      onPlayerSelect: handlePlayerSelect
                    }, null, 8, ["selected-player-name"]))
                  : _createCommentVNode("", true)
          ], 2))
        : (_unref(gameStore).isLoading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[3] || (_cache[3] = [
              _createElementVNode("p", null, "Загрузка комнаты...", -1)
            ])))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[5] || (_cache[5] = _createElementVNode("p", null, "Комната не найдена или произошла ошибка.", -1)),
              _createVNode(_component_router_link, {
                to: "/",
                class: "btn"
              }, {
                default: _withCtx(() => _cache[4] || (_cache[4] = [
                  _createTextVNode("Вернуться в лобби")
                ])),
                _: 1,
                __: [4]
              })
            ]))
  ], 2))
}
}

})